import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, Polyline, useMap } from 'react-leaflet';
import type { LatLngExpression } from 'leaflet';
import L from 'leaflet';
import FullscreenWrapper from '../common/FullscreenWrapper';
import PlatformIcon from '../common/PlatformIcon';

export type NearbySpot = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  description?: string;
  imageUrl?: string;
  address?: string;
};

const RecenterOnChange: React.FC<{ center: LatLngExpression }> = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center);
  }, [center, map]);
  return null;
};

// Component to handle map click to deselect
const MapClickHandler: React.FC<{ onMapClick: () => void }> = ({ onMapClick }) => {
  const map = useMap();
  useEffect(() => {
    const handler = () => onMapClick();
    map.on('click', handler);
    return () => { map.off('click', handler); };
  }, [map, onMapClick]);
  return null;
};

// Component to fit bounds only once on initial mount
const FitBoundsOnce: React.FC<{ bounds: [[number, number], [number, number]] | null }> = ({ bounds }) => {
  const map = useMap();
  const hasFitted = useRef(false);
  useEffect(() => {
    if (bounds && !hasFitted.current) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
      hasFitted.current = true;
    }
  }, [map, bounds]);
  return null;
};

// Component to render route lines with distance labels
const RouteLines: React.FC<{ spots: NearbySpot[] }> = ({ spots }) => {
  const map = useMap();
  
  useEffect(() => {
    if (!map || spots.length < 2) return;
    
    const markers: L.Marker[] = [];
    
    // Draw distance labels between consecutive spots
    for (let i = 1; i < spots.length; i++) {
      const prev = spots[i - 1];
      const curr = spots[i];
      const distance = calculateDistance(prev.lat, prev.lng, curr.lat, curr.lng);
      const distanceText = formatDistance(distance);
      
      // Calculate midpoint for label
      const midLat = (prev.lat + curr.lat) / 2;
      const midLng = (prev.lng + curr.lng) / 2;
      
      // Create distance label as a DivIcon
      const labelIcon = L.divIcon({
        className: '',
        html: `
          <div style="
            background: rgba(255, 255, 255, 0.95);
            border: 2px solid #0B61D6;
            border-radius: 12px;
            padding: 4px 10px;
            font-size: 12px;
            font-weight: 700;
            color: #0B61D6;
            box-shadow: 0 2px 8px rgba(11, 97, 214, 0.25);
            white-space: nowrap;
            pointer-events: none;
            line-height: 1.2;
            text-align: center;
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
          ">
            ${distanceText}
          </div>
        `,
        iconSize: [1, 1],
        iconAnchor: [0.5, 0.5],
      });
      
      const marker = L.marker([midLat, midLng], { icon: labelIcon }).addTo(map);
      markers.push(marker);
    }
    
    // Cleanup on unmount
    return () => {
      markers.forEach(marker => marker.remove());
    };
  }, [map, spots]);
  
  return null;
};

// Haversine 公式计算两点之间的距离（单位：米）
const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371e3; // 地球半径，单位：米
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // 返回距离，单位：米
};

// 格式化距离显示
const formatDistance = (meters: number): string => {
  if (meters < 1000) {
    return `${Math.round(meters)}米`;
  }
  return `${(meters / 1000).toFixed(1)}公里`;
};

const escapeHtmlAttr = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const safeLetter = (input?: string) => {
  if (!input) return '';
  const trimmed = input.trim();
  if (!trimmed) return '';
  const candidate = trimmed[0].toUpperCase();
  return escapeHtmlAttr(candidate);
};

const buildIcon = ({
  image,
  size,
  ringColor,
  fallbackLetter,
  highlight,
  numberLabel,
}: {
  image?: string;
  size: number;
  ringColor: string;
  fallbackLetter?: string;
  highlight?: boolean;
  numberLabel?: number | string;
}) => {
  const dimension = `${size}px`;
  const highlightBorderColor = '#F97316';
  const ring = highlight ? highlightBorderColor : '#ffffff';
  const shadow = highlight
    ? '0 12px 28px rgba(249,115,22,0.35)'
    : `0 10px 22px rgba(17,24,39,0.22)`;

  let innerHtml = '';
  if (image) {
    const escapedSrc = escapeHtmlAttr(image);
    if (typeof numberLabel !== 'undefined' && numberLabel !== null) {
      // render image with a small numeric badge overlay (top-left)
      const badgeSize = Math.max(14, Math.round(size * 0.36));
      innerHtml = `
        <div style="width:100%;height:100%;position:relative;">
          <img src="${escapedSrc}" alt="" style="width:100%;height:100%;object-fit:cover;display:block;" />
          <div style="position:absolute;left:6px;top:6px;background:rgba(0,0,0,0.6);color:#fff;border-radius:9999px;padding:2px 6px;font-weight:700;font-size:${badgeSize}px;line-height:1;">${escapeHtmlAttr(String(numberLabel))}</div>
        </div>
      `;
    } else {
      innerHtml = `<img src="${escapedSrc}" alt="" style="width:100%;height:100%;object-fit:cover;display:block;" />`;
    }
  } else {
    const letterOrNumber = (typeof numberLabel !== 'undefined' && numberLabel !== null) ? String(numberLabel) : safeLetter(fallbackLetter);
    innerHtml = `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:${highlight ? '#FEB273' : ringColor};color:#fff;font-weight:700;font-size:${Math.round(size * 0.42)}px;">${escapeHtmlAttr(letterOrNumber)}</div>`;
  }

  const indicator = highlight
    ? `<div style="position:absolute;bottom:-6px;right:-6px;width:${Math.round(size * 0.42)}px;height:${Math.round(size * 0.42)}px;border-radius:9999px;background:${highlightBorderColor};display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:${Math.max(12, Math.round(size * 0.28))}px;border:2px solid #fff;box-shadow:0 8px 18px rgba(249,115,22,0.35);">★</div>`
    : '';

  const html = `
    <div style="position:relative;width:${dimension};height:${dimension};display:flex;align-items:center;justify-content:center;">
      <div style="position:relative;width:100%;height:100%;border-radius:9999px;border:${highlight ? 4 : 3}px solid ${ring};overflow:hidden;box-shadow:${shadow};background:${ringColor};">
        ${innerHtml}
      </div>
      ${indicator}
    </div>
  `;

  return L.divIcon({ className: '', html, iconAnchor: [size / 2, size / 2] });
};

const NearbyMap: React.FC<{
  center: [number, number];
  spots: NearbySpot[];
  primaryLabel?: string;
  primaryImageUrl?: string;
  primaryName?: string;
  primaryAddress?: string;
  onSelectSpot?: (spotId: string) => void;
  // When true, show the total-distance badge in fullscreen header. Default: false.
  showTotal?: boolean;
  // When true, show all route lines by default. When false, only show line to selected spot. Default: false.
  showAllRoutes?: boolean;
  // When true, first click selects (shows line/distance), second click opens detail. Default: false (single click opens).
  requireDoubleClick?: boolean;
  // When true, show number labels (1, 2, 3...) on markers for route/itinerary display. Default: false.
  showNumberLabels?: boolean;
  // When true, hide the fullscreen expand button. Default: false.
  hideFullscreenButton?: boolean;
}> = ({
  center,
  spots,
  primaryLabel,
  primaryImageUrl,
  primaryName,
  primaryAddress,
  onSelectSpot,
  showTotal,
  showAllRoutes = false,
  requireDoubleClick = false,
  showNumberLabels = false,
  hideFullscreenButton = false,
}) => {
  console.log('[NearbyMap] Component render:', { spotsCount: spots.length, hasOnSelectSpot: !!onSelectSpot, requireDoubleClick, primaryLabel });
  if (typeof window === 'undefined') return null;
  const normalizedCenter: LatLngExpression = center;
  const hasSpots = spots.length > 0;
  const [expanded, setExpanded] = useState(false);
  const [sheetActive, setSheetActive] = useState(false);
  const [selectedSpotId, setSelectedSpotId] = useState<string | null>(null);
  const inlineMapRef = useRef<L.Map | null>(null);
  const fullscreenMapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    return () => {
      inlineMapRef.current = null;
      fullscreenMapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!expanded) return;
    const frame = window.requestAnimationFrame(() => setSheetActive(true));
    return () => {
      window.cancelAnimationFrame(frame);
      setSheetActive(false);
    };
  }, [expanded]);

  const toggleExpanded = useCallback((event?: React.MouseEvent<HTMLButtonElement>) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setExpanded((prev) => !prev);
  }, []);

  const closeExpanded = useCallback(() => {
    setExpanded(false);
  }, []);

  // Calculate total distance for route
  const totalDistance = useMemo(() => {
    if (spots.length < 2) return null;
    let total = 0;
    for (let i = 1; i < spots.length; i++) {
      const prev = spots[i - 1];
      const curr = spots[i];
      total += calculateDistance(prev.lat, prev.lng, curr.lat, curr.lng);
    }
    return formatDistance(total);
  }, [spots]);

  useEffect(() => {
    if (!expanded) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeExpanded();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [expanded, closeExpanded]);

  // Note: Don't manage body overflow here since parent components (RouteMapSheet, ItineraryEditor)
  // already handle scroll locking. Adding another layer causes conflicts.

  useEffect(() => {
    if (!expanded) return;
    const map = fullscreenMapRef.current;
    if (!map) return;
    const id = window.setTimeout(() => {
      map.invalidateSize({ animate: true });
    }, 280);
    return () => window.clearTimeout(id);
  }, [expanded]);

  const primaryIcon = useMemo(
    () => buildIcon({ image: primaryImageUrl, size: 48, ringColor: '#FB7A4A', fallbackLetter: primaryName, highlight: true }),
    [primaryImageUrl, primaryName]
  );

  // Calculate bounds to fit all spots
  const mapBounds = useMemo(() => {
    if (spots.length === 0) return null;
    
    const allLats = [center[0], ...spots.map(s => s.lat)];
    const allLngs = [center[1], ...spots.map(s => s.lng)];
    
    const minLat = Math.min(...allLats);
    const maxLat = Math.max(...allLats);
    const minLng = Math.min(...allLngs);
    const maxLng = Math.max(...allLngs);
    
    return [[minLat, minLng], [maxLat, maxLng]] as [[number, number], [number, number]];
  }, [spots, center]);

  const renderMap = (isExpandedView: boolean, targetRef: React.MutableRefObject<L.Map | null>) => (
    <MapContainer
      key={isExpandedView ? 'nearby-map-expanded' : 'nearby-map-inline'}
      center={normalizedCenter}
      zoom={hasSpots ? 12 : 13}
      style={{ height: isExpandedView ? '100%' : 320, width: '100%' }}
      scrollWheelZoom={isExpandedView}
      whenCreated={(map) => {
        targetRef.current = map;
      }}
    >
      <RecenterOnChange center={normalizedCenter} />
      <MapClickHandler onMapClick={() => setSelectedSpotId(null)} />
      <FitBoundsOnce bounds={mapBounds} />
      <TileLayer attribution="© OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <Marker position={normalizedCenter} icon={primaryIcon}>
        {primaryLabel ? <Popup>{primaryLabel}</Popup> : null}
        {(primaryLabel || primaryAddress) ? (
          <Tooltip direction="top" offset={[0, -24]} opacity={0.95} permanent={false} sticky>
            <div
              style={{
                maxWidth: isExpandedView ? 420 : 240,
                minWidth: isExpandedView ? 220 : 180,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                lineHeight: 1.45,
              }}
            >
              {primaryLabel ? <strong>{primaryLabel}</strong> : null}
              {primaryAddress ? <div style={{ marginTop: primaryLabel ? 6 : 0 }}>{primaryAddress}</div> : null}
            </div>
          </Tooltip>
        ) : null}
      </Marker>
      {(() => {
        console.log('[NearbyMap] Rendering spots:', spots.map((s, i) => ({ index: i+1, id: s.id, name: s.name })));
        return null;
      })()}
      {/* Draw route lines between spots - only when showAllRoutes is true */}
      {showAllRoutes && spots.length >= 2 && (
        <>
          <Polyline
            positions={spots.map(spot => [spot.lat, spot.lng] as [number, number])}
            pathOptions={{
              color: '#0B61D6',
              weight: 3,
              opacity: 0.7,
              dashArray: '10, 5',
            }}
          />
          <RouteLines spots={spots} />
        </>
      )}
      {/* Draw single line from center to selected spot */}
      {!showAllRoutes && selectedSpotId && (() => {
        const selectedSpot = spots.find(s => s.id === selectedSpotId);
        if (!selectedSpot) return null;
        const distance = calculateDistance(center[0], center[1], selectedSpot.lat, selectedSpot.lng);
        const distanceText = formatDistance(distance);
        const midLat = (center[0] + selectedSpot.lat) / 2;
        const midLng = (center[1] + selectedSpot.lng) / 2;
        return (
          <>
            <Polyline
              positions={[center, [selectedSpot.lat, selectedSpot.lng]]}
              pathOptions={{
                color: '#0B61D6',
                weight: 3,
                opacity: 0.8,
                dashArray: '10, 5',
              }}
            />
            <Marker
              position={[midLat, midLng]}
              icon={L.divIcon({
                className: '',
                html: `
                  <div style="
                    background: rgba(255, 255, 255, 0.95);
                    border: 2px solid #0B61D6;
                    border-radius: 12px;
                    padding: 4px 10px;
                    font-size: 12px;
                    font-weight: 700;
                    color: #0B61D6;
                    box-shadow: 0 2px 8px rgba(11, 97, 214, 0.25);
                    white-space: nowrap;
                    pointer-events: none;
                    line-height: 1.2;
                    text-align: center;
                    position: absolute;
                    left: 50%;
                    top: 50%;
                    transform: translate(-50%, -50%);
                  ">
                    ${distanceText}
                  </div>
                `,
                iconSize: [1, 1],
                iconAnchor: [0.5, 0.5],
              })}
            />
          </>
        );
      })()}
      {spots.map((spot, idx) => {
        const isSelected = spot.id === selectedSpotId;
        const icon = buildIcon({ 
          image: spot.imageUrl, 
          size: isSelected ? 44 : 36, 
          ringColor: isSelected ? '#F97316' : '#2563EB', 
          fallbackLetter: spot.name,
          numberLabel: showNumberLabels ? idx + 1 : undefined
        });
        
        return (
          <Marker
            key={spot.id}
            position={[spot.lat, spot.lng]}
            icon={icon}
            eventHandlers={{
              click: () => {
                console.log('[NearbyMap] Marker clicked:', { spotId: spot.id, requireDoubleClick, isSelected });
                if (requireDoubleClick) {
                  // Double-click mode: first click selects, second click opens
                  if (isSelected) {
                    onSelectSpot && onSelectSpot(spot.id);
                  } else {
                    setSelectedSpotId(spot.id);
                  }
                } else {
                  // Single-click mode: select and open immediately
                  setSelectedSpotId(spot.id);
                  onSelectSpot && onSelectSpot(spot.id);
                }
              },
            }}
          >
            <Tooltip direction="top" offset={[0, -18]} opacity={0.95} sticky>
              <div style={{ maxWidth: isExpandedView ? 380 : 230, minWidth: isExpandedView ? 200 : 160, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.45 }}>
                <strong>{spot.name || '景点'}</strong>
                {spot.address ? <div style={{ marginTop: 4 }}>{spot.address}</div> : null}
                {!spot.address && spot.description ? <div style={{ marginTop: 4 }}>{spot.description}</div> : null}
              </div>
            </Tooltip>
          </Marker>
        );
      })}
    </MapContainer>
  );

  return (
    <>
      <div
        style={{
          position: 'relative',
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 12px 26px rgba(15,23,42,0.18)',
          pointerEvents: expanded ? 'none' : 'auto',
          opacity: expanded ? 0.35 : 1,
          transition: 'opacity 0.18s ease',
        }}
      >
        {renderMap(false, inlineMapRef)}
        {/* Inline total-distance badge intentionally hidden in inline embed; shown only in fullscreen header */}
        {!hideFullscreenButton && (
        <button
          type="button"
          aria-label="全屏查看地图"
          onClick={toggleExpanded}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 40,
            height: 40,
            borderRadius: 9999,
            border: 'none',
            background: 'rgba(15,23,42,0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 12px 24px rgba(15,23,42,0.28)',
            color: '#fff',
            transition: 'transform 0.18s ease, background 0.18s ease',
            zIndex: 999,
          }}
          onMouseEnter={(event) => {
            event.currentTarget.style.transform = 'scale(1.05)';
            event.currentTarget.style.background = 'rgba(15,23,42,0.98)';
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.transform = 'scale(1)';
            event.currentTarget.style.background = 'rgba(15,23,42,0.92)';
          }}
        >
          <PlatformIcon name="fullscreen" size={20} color="#fff" family="MaterialIcons" />
        </button>
        )}
      </div>
      <FullscreenWrapper
        visible={expanded}
        onClose={closeExpanded}
        maxWidth="100%"
        contentStyle={{
          backgroundColor: 'transparent',
          boxShadow: 'none',
          padding: 0,
          width: '100%',
          maxWidth: '100%',
          alignSelf: 'stretch',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: '100%',
            margin: 0,
            background: '#0F172A',
            color: '#fff',
            borderTopLeftRadius: 26,
            borderTopRightRadius: 26,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            maxHeight: '100vh',
            overflow: 'hidden',
            boxShadow: '0 32px 56px rgba(15,23,42,0.45)',
            transform: sheetActive ? 'translateY(0)' : 'translateY(56px)',
            opacity: sheetActive ? 1 : 0,
            transition: 'transform 0.28s ease, opacity 0.28s ease',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              padding: '20px 24px 12px',
              background: 'linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(15,23,42,0.65) 100%)',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              minHeight: 84,
              gap: 16,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {primaryLabel ? (
                  <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>{primaryLabel}</div>
                ) : null}
                {(showTotal && totalDistance) && (
                  <div
                    style={{
                      padding: '6px 12px',
                      borderRadius: 9999,
                      background: 'rgba(251,122,74,0.95)',
                      border: '1px solid rgba(249,115,22,0.3)',
                      boxShadow: '0 4px 12px rgba(249,115,22,0.3)',
                      fontSize: 12,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      whiteSpace: 'nowrap',
                      fontWeight: 600,
                    }}
                  >
                    <span>🚶</span>
                    <span>总路程 {totalDistance}</span>
                  </div>
                )}
              </div>
              {primaryAddress ? (
                <div style={{ marginTop: 6, fontSize: 14, lineHeight: 1.5, color: 'rgba(255,255,255,0.72)' }}>{primaryAddress}</div>
              ) : null}
            </div>
            <button
              type="button"
              aria-label="关闭全屏地图"
              onClick={closeExpanded}
              style={{
                width: 42,
                height: 42,
                borderRadius: 9999,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(15,23,42,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#fff',
                transition: 'background 0.2s ease, transform 0.2s ease',
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.transform = 'scale(1.05)';
                event.currentTarget.style.background = 'rgba(15,23,42,0.85)';
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.transform = 'scale(1)';
                event.currentTarget.style.background = 'rgba(15,23,42,0.6)';
              }}
            >
              <PlatformIcon name="fullscreen-exit" size={22} color="#fff" family="MaterialIcons" />
            </button>
          </div>
          <div style={{ flex: 1, position: 'relative', background: '#020617' }}>
            {renderMap(true, fullscreenMapRef)}
          </div>
        </div>
      </FullscreenWrapper>
    </>
  );
};

export default NearbyMap;
